import type { ButtonProps } from '@seen/ui'
import { cn } from '@seen/ui'
import { IconCheck } from '@tabler/icons-react'
import {
  type FocusEventHandler,
  type ForwardedRef,
  isValidElement,
  type JSX,
  type ReactElement,
  type ReactNode,
  useCallback,
  useMemo,
} from 'react'
import {
  BaseCombobox,
  type NormalizedOption,
  type Option,
  type ValueFromOption,
} from '#src/components/core/combobox/BaseCombobox.tsx'
import type { PopoverContentProps } from '#src/components/core/popover/PopoverContent.tsx'
import { forwardRef } from '#src/utils/forwardRef.ts'

export interface MultiSelectProps<T extends Option> {
  options: T[]
  value: ValueFromOption<T>[] | null | undefined
  onChange?: (value: ValueFromOption<T>[]) => any
  onBlur?: FocusEventHandler<HTMLButtonElement | HTMLDivElement>
  align?: PopoverContentProps['align']
  variant?: ButtonProps['variant']
  name?: ButtonProps['name']
  disabled?: ButtonProps['disabled']
  loading?: ButtonProps['loading']
  color?: ButtonProps['color']
  className?: ButtonProps['className']
  disableSearch?: boolean
  // A ReactElement placeholder renders as-is (no muted-span wrapper), so a
  // custom trigger can keep its layout in the empty state.
  placeholder?: ReactNode
  children?: ReactElement<any> | string
  renderAccessory?: (
    option: NormalizedOption<ValueFromOption<T>>
  ) => ReactNode | string
  badgeLabel?: (option: NormalizedOption<ValueFromOption<T>>) => string
  maxDisplayed?: number
}

export const MultiSelect = forwardRef(MultiSelectWithRef)

function MultiSelectWithRef<T extends Option>(
  props: MultiSelectProps<T>,
  ref: ForwardedRef<HTMLButtonElement>
): JSX.Element {
  const {
    options,
    value = [],
    onChange,
    variant = 'outline',
    children,
    renderAccessory,
    badgeLabel,
    placeholder,
    maxDisplayed = 1,
    ...rest
  } = props

  const normalizedOptions = useMemo(
    () =>
      options.map((o) => {
        const normalizedOption =
          typeof o === 'string' ? { value: o, label: o } : o
        return normalizedOption as NormalizedOption<ValueFromOption<T>>
      }),
    [options]
  )

  const selectedOptions = useMemo(() => {
    if (!value?.length) {
      return []
    }
    return value
      .map((v) => normalizedOptions.find((option) => option.value === v))
      .filter((v): v is NormalizedOption<ValueFromOption<T>> => v !== undefined)
  }, [value, normalizedOptions])

  const renderTrigger = useCallback(() => {
    if (!selectedOptions.length) {
      if (isValidElement(placeholder)) {
        return placeholder
      }
      return (
        <span className={cn(variant !== 'default' && 'text-muted-foreground')}>
          {placeholder ?? 'Search...'}
        </span>
      )
    }
    return (
      <>
        {children ?? (
          <div className="flex flex-wrap gap-1">
            {selectedOptions.slice(0, maxDisplayed).map((option) => (
              <p key={option.value}>
                {badgeLabel ? badgeLabel(option) : option.label}
              </p>
            ))}
            {selectedOptions.length > maxDisplayed && (
              <span className="ml-2 text-sm text-muted-foreground">{`+${selectedOptions.length - maxDisplayed}`}</span>
            )}
          </div>
        )}
      </>
    )
  }, [
    selectedOptions,
    children,
    variant,
    placeholder,
    badgeLabel,
    maxDisplayed,
  ])

  const renderItem = useCallback(
    ({ option }: { option: NormalizedOption<ValueFromOption<T>> }) => (
      <>
        <IconCheck
          className={cn(
            'mr-2 size-4 shrink-0',
            value?.some((v) => v === option.value) ? 'opacity-100' : 'opacity-0'
          )}
        />
        {option.label}
        {renderAccessory?.(option)}
      </>
    ),
    [value, renderAccessory]
  )

  return (
    <BaseCombobox
      ref={ref}
      options={options}
      variant={variant}
      renderTrigger={renderTrigger}
      renderItem={renderItem}
      onSelect={(selectedValue: ValueFromOption<T>) => {
        const newValue = value ?? []
        const index = newValue.indexOf(selectedValue)
        if (index === -1) {
          onChange?.([...newValue, selectedValue])
        } else {
          onChange?.([
            ...newValue.slice(0, index),
            ...newValue.slice(index + 1),
          ])
        }
        return true
      }}
      {...rest}
    />
  )
}
